import { useState } from 'react';
import {
  Key,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../services/api';

interface SettingsProps {
  // Called after a successful password change so the session can be torn down —
  // the old password is gone, so the user signs in again with the new one.
  onPasswordChanged: () => void;
}

export default function Settings({ onPasswordChanged }: SettingsProps) {
  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setIsChangingPassword(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed. Signing you out — please sign in with your new password.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Give the toast a moment to register before the login screen replaces this view.
      setTimeout(onPasswordChanged, 1800);
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };


  return (
    <div className="space-y-6">

      {/* App Configuration */}
      {/* <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">App Configuration</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">App Name</label>
            <input
              type="text"
              value={settings.appName}
              onChange={(e) => setSettings({ ...settings, appName: e.target.value })}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">App Description</label>
            <textarea
              value={settings.appDescription}
              onChange={(e) => setSettings({ ...settings, appDescription: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">App Version</label>
            <input
              type="text"
              value={settings.appVersion}
              onChange={(e) => setSettings({ ...settings, appVersion: e.target.value })}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
            />
            <p className="text-sm text-slate-500 mt-1">Current version displayed in the app</p>
          </div>
        </div>
      </div> */}

      {/* Appearance */}
      {/* <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Palette className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">Appearance</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Primary Color</label>
            <div className="flex gap-3 items-center">
              <input
                type="color"
                value={settings.primaryColor}
                onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                className="w-16 h-12 border-2 border-slate-200 rounded-xl cursor-pointer p-1"
              />
              <input
                type="text"
                value={settings.primaryColor}
                onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                placeholder="#2B7A7E"
                className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] font-mono"
              />
            </div>
            <p className="text-sm text-slate-500 mt-1">Used for buttons, links, and highlights</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Accent Color</label>
            <div className="flex gap-3 items-center">
              <input
                type="color"
                value={settings.accentColor}
                onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                className="w-16 h-12 border-2 border-slate-200 rounded-xl cursor-pointer p-1"
              />
              <input
                type="text"
                value={settings.accentColor}
                onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                placeholder="#D4AF37"
                className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] font-mono"
              />
            </div>
            <p className="text-sm text-slate-500 mt-1">Used for secondary actions and badges</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl md:col-span-2">
            <div>
              <p className="font-semibold text-slate-800">Dark Mode</p>
              <p className="text-sm text-slate-600">Enable dark mode as default for the app</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.darkModeEnabled}
                onChange={(e) => setSettings({ ...settings, darkModeEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-8 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:bg-[#2B7A7E] after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all"></div>
            </label>
          </div>
        </div>

        <div className="mt-4 p-4 bg-slate-50 rounded-xl">
          <p className="text-sm font-semibold text-slate-700 mb-3">Preview</p>
          <div className="flex gap-3 flex-wrap">
            <div
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: settings.primaryColor }}
            >
              Primary Button
            </div>
            <div
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: settings.accentColor }}
            >
              Accent Button
            </div>
            <div
              className="px-4 py-2 rounded-lg text-sm font-medium border-2"
              style={{ borderColor: settings.primaryColor, color: settings.primaryColor }}
            >
              Outlined
            </div>
          </div>
        </div>
      </div> */}

      {/* Change Password */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Lock className="w-6 h-6 text-teal-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">Change Password</h3>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Current Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type={showCurrentPwd ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
                className="w-full pl-11 pr-11 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] transition-all"
              />
              <button type="button" onClick={() => setShowCurrentPwd(!showCurrentPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">New Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                required
                className="w-full pl-11 pr-11 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] transition-all"
              />
              <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm New Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type={showConfirmPwd ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full pl-11 pr-11 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] transition-all"
              />
              <button type="button" onClick={() => setShowConfirmPwd(!showConfirmPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isChangingPassword}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChangingPassword ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <Key className="w-4 h-4" />
                Change Password
              </>
            )}
          </button>
        </form>
      </div>

      {/* Sticky Save Bar */}
      {/* <div className="sticky bottom-0 bg-gradient-to-r from-[#2B7A7E] to-[#236266] rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-bold mb-1">Save Your Changes</h4>
            <p className="text-sm text-white/80">App Configuration and Appearance changes are saved to the backend</p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-8 py-3 bg-white text-[#2B7A7E] rounded-xl hover:bg-slate-100 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-[#2B7A7E] border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Save All Settings
              </>
            )}
          </button>
        </div>
      </div> */}
    </div>
  );
}
